import { OfferItem } from '../../../shared/types';

import calculateMeansByGroup from '../utils/calculateMeansByGroup';
import _ from 'lodash';
import { DatabaseOfferWithItems } from '../../../shared/types/offers';
import { calculateItemPrice } from '../../../shared/helpers/calculateItemPrice';
import { getWarningCounts } from '../pages/OfferSheets/helpers';
import {
  AcceptanceMatrixRow,
  approvalValidatorBase,
  commonValidator,
  CorporateTitle,
  ValidationValue,
} from './approvalValidatorCommon';
import { getOfferTotalCoefficients } from '../../../shared/helpers/getOfferTotalCoefficients';
import { PRICE_FACTOR } from '../../../shared/constants';
import { isCriticalPrice } from '../utils/pricingSheetRowFilters';

export enum AcceptanceRequirementsOffer {
  PriceCoefficient,
  PercentageDiscount,
  CriticalEquipment,
  TotalPriceCoefficient,
  TotalPriceCoefficientWhenCritical,
}

const acceptanceMatrixRows: AcceptanceMatrixRow<
  AcceptanceRequirementsOffer,
  DatabaseOfferWithItems,
  OfferItem
>[] = [
  {
    reason: AcceptanceRequirementsOffer.TotalPriceCoefficient,
    label: 'Tarjous sisältää hintakertoimen 1 alituksia',
    validator: commonValidator,
    getValueForValidation: (_, offerSheetItems) => {
      const { belowOneCoefficient } =
        getWarningCounts(offerSheetItems);

      return belowOneCoefficient;
    },
    formatValue: (value) => {
      if (!value) {
        return '0 kpl';
      }

      let displayValue = `${value + ' kpl'}`;

      return displayValue;
    },
    requirements: {
      [CorporateTitle.AccountManager]: undefined,
      [CorporateTitle.SalesManager]: {
        minValue: 1,
        maxValue: 10,
        label: 'Alle 10kpl',
      },
      [CorporateTitle.RegionalManager]: {
        minValue: 10,
        maxValue: 15,
        label: 'Alle 15kpl',
      },
      [CorporateTitle.SalesDirector]: {
        minValue: 15,
        maxValue: 20,
        label: 'Alle 20kpl',
      },
      [CorporateTitle.CEO]: {
        minValue: 20,
        maxValue: Infinity,
        label: 'Yli 20kpl',
      },
    },
  },
  {
    reason: AcceptanceRequirementsOffer.PercentageDiscount,
    label:
      'Tarjous sisältää hintoja, jotka ovat alle 70% ehdotushinnasta',
    validator: commonValidator,
    getValueForValidation: (_, offerSheetItems) => {
      const { below70 } = getWarningCounts(offerSheetItems);
      return below70;
    },
    formatValue: (value) => {
      if (!value) {
        return '0 kpl';
      }

      let displayValue = `${value + ' kpl'}`;

      return displayValue;
    },
    requirements: {
      [CorporateTitle.AccountManager]: undefined,
      [CorporateTitle.SalesManager]: {
        minValue: 1,
        maxValue: 10,
        label: 'Alle 10kpl',
      },
      [CorporateTitle.RegionalManager]: {
        minValue: 10,
        maxValue: 15,
        label: 'Alle 15kpl',
      },
      [CorporateTitle.SalesDirector]: {
        minValue: 15,
        maxValue: 20,
        label: 'Alle 20kpl',
      },
      [CorporateTitle.CEO]: {
        minValue: 20,
        maxValue: Infinity,
        label: 'Yli 20kpl',
      },
    },
  },
  {
    reason: AcceptanceRequirementsOffer.CriticalEquipment,
    label: 'Kriittisen hinnan alitus ja pienin hintakerroin on',
    validator: commonValidator,
    getValueForValidation: (offerSheet, offerItems) => {
      const criticalItems = offerItems.filter(isCriticalPrice);
      const minValue = _.min(
        criticalItems.map((critical) => {
          return getOfferTotalCoefficients([critical]);
        }),
      );
      return minValue ? minValue / PRICE_FACTOR : undefined;
    },
    formatValue: (value) => {
      if (!value || typeof value !== 'number') {
        return '-';
      }

      let displayValue = `${value.toFixed(2)}`;

      return displayValue;
    },
    requirements: {
      [CorporateTitle.AccountManager]: undefined,
      [CorporateTitle.SalesManager]: {
        minValue: 2,
        maxValue: Infinity,
        label: 'Yli 2',
      },
      [CorporateTitle.RegionalManager]: {
        minValue: 1.5,
        maxValue: 2,
        label: 'Alle 2',
      },
      [CorporateTitle.SalesDirector]: {
        minValue: 1,
        maxValue: 1.5,
        label: 'Alle 1,5',
      },
      [CorporateTitle.CEO]: {
        minValue: 0,
        maxValue: 1,
        label: 'Alle 1',
      },
    },
  },
  {
    reason: AcceptanceRequirementsOffer.TotalPriceCoefficient,
    label: 'Tarjouksen kokonaishintakerroin',
    validator: commonValidator,
    getValueForValidation: (offerSheet) => {
      const totalCoefficient = calculateItemPrice(
        offerSheet.totalCoefficient,
      );
      return totalCoefficient;
    },
    formatValue: (value) => {
      if (!value || typeof value !== 'number') {
        return '-';
      }

      let displayValue = `${value.toFixed(2)}`;

      return displayValue;
    },
    requirements: {
      [CorporateTitle.AccountManager]: undefined,
      [CorporateTitle.SalesManager]: {
        minValue: 1.5,
        maxValue: 2,
        label: 'Alle 2',
      },
      [CorporateTitle.RegionalManager]: {
        minValue: 1,
        maxValue: 1.5,
        label: 'Alle 1,5',
      },
      [CorporateTitle.SalesDirector]: {
        minValue: 0,
        maxValue: 1,
        label: 'Alle 1',
      },
      [CorporateTitle.CEO]: undefined,
    },
  },
  {
    reason: AcceptanceRequirementsOffer.TotalPriceCoefficient,
    label:
      'Tarjouksen kokonaishintakerroin kun kriittisen hinnan alitus',
    validator: commonValidator,
    getValueForValidation: (offerSheet, offerSheetItems) => {
      const totalCoefficient = calculateItemPrice(
        offerSheet.totalCoefficient,
      );

      const containsCritical = offerSheetItems.some(isCriticalPrice);

      if (!containsCritical) {
        return undefined;
      }

      return totalCoefficient;
    },
    formatValue: (value) => {
      if (!value || typeof value !== 'number') {
        return '-';
      }

      let displayValue = `${value.toFixed(2)}`;

      return displayValue;
    },
    requirements: {
      [CorporateTitle.AccountManager]: undefined,
      [CorporateTitle.SalesManager]: {
        minValue: 2,
        maxValue: 2.5,
        label: 'Alle 2,5',
      },
      [CorporateTitle.RegionalManager]: {
        minValue: 1.5,
        maxValue: 2,
        label: 'Alle 2',
      },
      [CorporateTitle.SalesDirector]: {
        minValue: 1,
        maxValue: 1.5,
        label: 'Alle 1,5',
      },
      [CorporateTitle.CEO]: {
        minValue: 0,
        maxValue: 1,
        label: 'Alle 1',
      },
    },
  },
];

type Result = {
  value: ValidationValue;
  displayValue: string;
  reasonLabel: string;
  requirements: Record<string, any>;
};

export type ValidatorResult = {
  result: Result[];
  requiredApprovalLevel?: CorporateTitle;
  requiresApproval: boolean;
};

export const approvalValidatorOffer = (
  offerSheet: DatabaseOfferWithItems,
  offerSheetItems: OfferItem[],
): ValidatorResult => {
  return approvalValidatorBase(
    offerSheet,
    offerSheetItems,
    acceptanceMatrixRows,
  );
};
